import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { Skeleton } from "@/components/ui/skeleton";

const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const AnalysisResults = lazy(() => import("./pages/AnalysisResults"));
const AnalysisHistory = lazy(() => import("./pages/AnalysisHistory"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6">
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <div className="flex gap-3">
          <Skeleton className="h-24 flex-1 rounded-lg" />
          <Skeleton className="h-24 flex-1 rounded-lg" />
        </div>
        <Skeleton className="h-4 w-2/3 mx-auto rounded" />
      </div>
      <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  /* OAuth returns with hash #session_id=… — complete login here (do not strip hash). */
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/analyze" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      <Route path="/dashboard" element={<Navigate to="/analyze" replace />} />

      <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly={true}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/analytics" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/analyses" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/transactions" element={<Navigate to="/admin/dashboard" replace />} />

      <Route path="/user/dashboard" element={<Navigate to="/analyze" replace />} />
      <Route path="/analyze" element={<UserDashboard />} />
      <Route path="/websites" element={<Navigate to="/dashboard" replace />} />
      <Route path="/history" element={<ProtectedRoute adminOnly={true}><AnalysisHistory /></ProtectedRoute>} />
      <Route path="/browser-snapshot" element={<Navigate to="/analyze" replace />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/health-dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/health-dashboard/:websiteUrl" element={<Navigate to="/dashboard" replace />} />
      <Route path="/schedules" element={<Navigate to="/dashboard" replace />} />
      <Route path="/webhooks" element={<Navigate to="/dashboard" replace />} />

      <Route path="/analysis/:id" element={<AnalysisResults />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
              <AppRouter />
            </Suspense>
          </ErrorBoundary>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
